import {
  Component,
  type CSSProperties,
  type ErrorInfo,
  type ReactNode,
} from 'react'

/**
 * Error boundary for the route-level lazy chunks (CodeRabbit #468
 * round 1).
 *
 * Wrapping the route ``<Suspense>`` block from ``App.tsx``. The
 * Suspense boundary handles the PENDING state of a lazy import; this
 * boundary handles the REJECTED state. The two are complementary
 * mechanisms in React's concurrent-render model — Suspense alone
 * doesn't catch errors, and an ErrorBoundary alone doesn't render
 * the loading placeholder during a chunk fetch.
 *
 * This boundary catches TWO structurally different failures, and the
 * whole point of this module is that they get DIFFERENT treatment —
 * conflating them (the pre-#1115 behaviour) showed every crash as a
 * misleading "please reload" notice and required a manual click even
 * for the benign case:
 *
 * 1. **Stale-chunk-hash 404 after a deploy** — the recoverable case.
 *      1. User loaded ``dev.eeh.ae`` ~10 minutes ago. Their tab is
 *         running ``index-OLD.js`` with a dynamic-import call to
 *         ``./pages/TeacherDashboard-OLD-HASH.js``.
 *      2. A deploy lands in between. The new ``index.html`` references
 *         ``index-NEW.js`` and ``TeacherDashboard-NEW-HASH.js``
 *         replaces the old chunk on the CDN.
 *      3. User navigates in their stale tab. The cached
 *         ``index-OLD.js`` calls
 *         ``import('./pages/TeacherDashboard-OLD-HASH.js')`` — the
 *         CDN 404s because the chunk no longer exists, and the lazy
 *         import rejects.
 *    The fix is purely "re-fetch ``index.html`` to pick up the new
 *    hashes", so we do that AUTOMATICALLY (``location.reload()``)
 *    rather than asking the user to. ``index.html`` is served
 *    ``Cache-Control: max-age=0, must-revalidate`` (see
 *    ``public/_headers`` — the hashed assets are ``immutable``, the
 *    document is not), so one reload reliably lands the new bundle.
 *
 *    The auto-reload is guarded by a *timestamped* ``sessionStorage``
 *    stamp so a *genuinely* broken deploy — a chunk that 404s even
 *    from a fresh ``index.html`` — can't spin in a reload loop. A
 *    broken chunk re-errors within milliseconds of the reload, so if
 *    the stamp is younger than ``AUTO_RELOAD_WINDOW_MS`` we do NOT
 *    reload again — we fall through to the manual "please reload"
 *    prompt (the ``stale`` phase). The stamp then ages out of that
 *    window on its own, so a *later* deploy's stale chunk (minutes or
 *    hours later, or in a fresh tab) auto-recovers again with no
 *    bookkeeping.
 *
 *    Crucially we must NOT clear the stamp on a clean mount, even
 *    though that looks tidier. This boundary wraps ``<Suspense>`` and
 *    mounts ONCE at app root, on the first commit — and that commit
 *    happens with ``phase: 'ok'`` while the lazy chunk is still
 *    *fetching*, i.e. BEFORE its async 404 rejection is observed. A
 *    clean-mount clear would therefore wipe the stamp on every reload
 *    *before* the error recurred, so the guard could never survive a
 *    reload and a broken deploy would loop forever. The time window —
 *    not a mount-lifecycle clear — is what makes the guard both
 *    self-resetting and loop-proof.
 *
 * 2. **Any other render-time crash** inside a lazy route — a real bug,
 *    NOT a deploy artifact. Reloading won't help, so we must not tell
 *    the user it will. This renders a generic error state (the
 *    ``crash`` phase) and surfaces the error rather than disguising it
 *    as a version notice.
 *
 * Korean primary + English mirror per the project i18n convention
 * (PrivacyFooter, RevocationBanner, EMPTY_PDF_ERROR, etc. all
 * follow this). Strings are hardcoded rather than routed through
 * ``useT()`` on purpose: this boundary must stay renderable even if
 * the failure that tripped it was the i18n bundle itself.
 *
 * Implementation is a class component because React's
 * Hooks API still doesn't expose ``getDerivedStateFromError`` /
 * ``componentDidCatch``. The official ``Sentry.ErrorBoundary``
 * wrapper exists but pulls the Sentry SDK into the boundary's
 * eager-loaded module — we'd lose the deferred-Sentry pattern
 * (``main.tsx`` + ``sentry.ts``) that this PR's perf work
 * preserves. Keeping a hand-rolled boundary keeps Sentry out of
 * the synchronous boot path.
 */

/**
 * ``sessionStorage`` key holding the ``Date.now()`` of the last
 * auto-reload. Session-scoped (not ``localStorage``) on purpose: a
 * stale chunk is a per-tab condition, and the stamp should die with
 * the tab rather than linger across days.
 */
const AUTO_RELOAD_GUARD_KEY = 'eehae:routeErrorBoundary:autoReloaded'

/**
 * How recently an auto-reload must have happened for us to treat the
 * chunk failure as "already tried, don't loop". A stale chunk that's
 * broken on a *fresh* ``index.html`` re-throws within milliseconds of
 * the reload, so a few seconds is plenty to catch the re-error; a
 * genuine *later* stale chunk lands well outside this window and is
 * allowed to auto-recover. (Sub-cliff per the project timeout
 * philosophy: too short → reload loop, too long → a manual prompt
 * where an auto-reload would have done — so we err slightly long.)
 */
const AUTO_RELOAD_WINDOW_MS = 10_000

/**
 * Did we auto-reload within the last ``AUTO_RELOAD_WINDOW_MS``? If so
 * the chunk is *still* failing right after a reload — a broken deploy,
 * so we must NOT reload again.
 *
 * Read-only — safe to call from the (otherwise pure)
 * ``getDerivedStateFromError``. Fail-safe to ``true`` (don't reload)
 * whenever we can't trust the stamp: ``sessionStorage`` unreachable
 * (sandboxed iframe / hardened privacy mode), or an unparseable value.
 * The cost of a false ``true`` is one manual prompt; the cost of a
 * false ``false`` is an infinite reload loop, so we bias hard toward
 * ``true``.
 */
function autoReloadAlreadyAttempted(): boolean {
  try {
    if (typeof window === 'undefined') return true
    const raw = window.sessionStorage.getItem(AUTO_RELOAD_GUARD_KEY)
    if (raw == null) return false
    const stampedAt = Number(raw)
    if (!Number.isFinite(stampedAt)) return true
    return Date.now() - stampedAt < AUTO_RELOAD_WINDOW_MS
  } catch {
    return true
  }
}

/**
 * True when ``error`` is a failed dynamic ``import()`` of a route
 * chunk — i.e. the recoverable stale-chunk-hash case, not a real
 * render crash.
 *
 * Browsers word this rejection differently and there's no shared
 * error ``name`` across engines, so we match on the documented
 * message text of each:
 *   - Chromium: "Failed to fetch dynamically imported module: <url>"
 *   - Firefox:  "error loading dynamically imported module: <url>"
 *   - Safari:   "Importing a module script failed."
 * ``ChunkLoadError`` (the webpack name) is matched too as a
 * belt-and-suspenders guard in case the bundler ever changes.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name === 'ChunkLoadError') return true
  const message = error.message || ''
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

/**
 * - ``ok``        — no error; render children.
 * - ``reloading`` — recoverable stale chunk; an auto-reload is in
 *                   flight, so render a neutral placeholder (we're
 *                   about to navigate away anyway).
 * - ``stale``     — stale chunk that re-failed inside the auto-reload
 *                   window (or storage was unavailable to guard it):
 *                   show the manual reload prompt.
 * - ``crash``     — a non-chunk render error: show a true error state.
 */
type Phase = 'ok' | 'reloading' | 'stale' | 'crash'

interface State {
  phase: Phase
  /** Saved error for diagnostic display + Sentry hand-off. */
  error: Error | null
}

interface Props {
  children: ReactNode
}

class RouteErrorBoundary extends Component<Props, State> {
  state: State = { phase: 'ok', error: null }

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error)) {
      // Fresh stale-chunk hit → auto-reload (``reloading``). If we
      // already reloaded inside the window (the chunk is broken on a
      // fresh index.html too) or can't guard the loop, degrade to the
      // manual prompt (``stale``).
      return {
        phase: autoReloadAlreadyAttempted() ? 'stale' : 'reloading',
        error,
      }
    }
    return { phase: 'crash', error }
  }

  // NB: deliberately no componentDidMount guard-clear — see the
  // class doc above. This boundary mounts cleanly (phase 'ok') before
  // the async chunk rejection arrives, so clearing the stamp on mount
  // would wipe it on every reload and let a broken deploy loop. The
  // time window in autoReloadAlreadyAttempted() resets the guard instead.

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Best-effort report to Sentry. The deferred-init pattern from
    // ``main.tsx``/``sentry.ts`` means the SDK may or may not be
    // loaded depending on render timing — we guard with
    // ``in window`` so the error boundary itself never throws when
    // Sentry isn't initialized. The window-level Sentry global is
    // set by ``initSentry`` in sentry.ts after the SDK loads.
    if (typeof window !== 'undefined' && 'Sentry' in window) {
      // The ``Sentry`` global is attached at runtime by
      // ``initSentry()`` in src/sentry.ts; the Window type doesn't
      // declare it. The double cast (``unknown`` then to the shape
      // we actually use) is the standard TypeScript escape hatch for
      // "I know this property exists at runtime even though the type
      // says otherwise". Defensive try/catch is the contract: an
      // error boundary must never throw — if Sentry's API shape
      // changes upstream or its capture call fails, we still want
      // the visible fallback to render.
      try {
        ;(window as unknown as {
          Sentry: {
            captureException: (e: Error, ctx?: unknown) => void
          }
        }).Sentry.captureException(error, {
          contexts: { react: { componentStack: info.componentStack } },
          // Tag the recoverable-vs-real split so the two don't blur
          // together in Sentry — a flood of stale-chunk reloads after
          // a deploy shouldn't drown out a genuine crash.
          tags: { route_error_kind: this.state.phase },
        })
      } catch {
        // Swallow — see above. console.error below already logged it.
      }
    }
    // Always log to console so dev-tools surfaces the stack with the
    // sourcemap-decoded version even when Sentry isn't loaded. (The
    // project's eslint config doesn't gate ``no-console``, so no
    // disable directive is needed.)
    console.error('[RouteErrorBoundary] uncaught render error:', error, info)

    // Fresh stale chunk → stamp the reload time and reload to pick up
    // the new asset hashes. Done here (a commit-phase lifecycle)
    // rather than in getDerivedStateFromError, which must stay free of
    // side effects. The stamp is what autoReloadAlreadyAttempted()
    // reads on the *next* load to decide whether the chunk is still
    // broken (→ stop) or this is a later, recoverable failure (→ go).
    if (this.state.phase === 'reloading' && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(
          AUTO_RELOAD_GUARD_KEY,
          String(Date.now()),
        )
      } catch {
        // Couldn't persist the guard → an auto-reload could loop, so
        // don't risk it. Fall back to the manual prompt instead.
        this.setState({ phase: 'stale' })
        return
      }
      window.location.reload()
    }
  }

  handleReload = (): void => {
    // Full reload (not a router push) — re-fetch index.html so the
    // browser picks up the new asset hashes. ``location.reload()``
    // bypasses the React tree entirely; the parent Suspense fallback
    // will briefly show while the new bundle parses.
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  render(): ReactNode {
    const { phase, error } = this.state

    if (phase === 'ok') {
      return this.props.children
    }

    // While an auto-reload is in flight we're about to navigate away,
    // so show a quiet, non-alarming placeholder rather than the scary
    // full-screen alert. role="status" (polite) — this is informational.
    if (phase === 'reloading') {
      return (
        <div
          role="status"
          aria-live="polite"
          style={containerStyle}
        >
          <div style={{ maxWidth: '32rem' }}>
            <p style={{ fontSize: '1rem', opacity: 0.85 }}>
              최신 버전으로 업데이트하고 있어요…
              <br />
              Updating to the latest version…
            </p>
          </div>
        </div>
      )
    }

    // Inline styles rather than CSS Modules — the boundary should
    // remain renderable even if a CSS-loading failure was part of
    // the original crash. Visual treatment is intentionally plain;
    // matches the "low-impact toast" shape of RevocationBanner.
    if (phase === 'stale') {
      return (
        <div role="alert" aria-live="assertive" style={containerStyle}>
          <div style={{ maxWidth: '32rem' }}>
            <h1 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>
              새로운 버전이 배포되었어요. 페이지를 새로고침 해주세요.
            </h1>
            <p
              style={{
                fontSize: '0.95rem',
                marginBottom: '1.5rem',
                opacity: 0.85,
              }}
            >
              A new version has been deployed. Please reload the page.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={buttonStyle}
            >
              새로고침 / Reload
            </button>
          </div>
        </div>
      )
    }

    // phase === 'crash' — a genuine render error. Do NOT claim a new
    // version is available (reloading likely won't fix a real bug);
    // show a generic error state and surface the message for triage.
    return (
      <div role="alert" aria-live="assertive" style={containerStyle}>
        <div style={{ maxWidth: '32rem' }}>
          <h1 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            문제가 발생했어요.
          </h1>
          <p
            style={{
              fontSize: '0.95rem',
              marginBottom: '1.5rem',
              opacity: 0.85,
            }}
          >
            Something went wrong while loading this page. Reloading may
            help; if it keeps happening, please let us know.
          </p>
          {error?.message ? (
            <p
              style={{
                fontSize: '0.8rem',
                marginBottom: '1.5rem',
                opacity: 0.6,
                fontFamily: 'monospace',
                wordBreak: 'break-word',
              }}
            >
              {error.message}
            </p>
          ) : null}
          <button
            type="button"
            onClick={this.handleReload}
            style={buttonStyle}
          >
            새로고침 / Reload
          </button>
        </div>
      </div>
    )
  }
}

const containerStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  textAlign: 'center',
  color: 'var(--text, #1a1a1a)',
  background: 'var(--bg, #ffffff)',
}

const buttonStyle: CSSProperties = {
  padding: '0.625rem 1.5rem',
  fontSize: '1rem',
  border: '1px solid currentColor',
  borderRadius: '0.5rem',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
}

export default RouteErrorBoundary
